import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'

import {
  getInhabitancyEmailRecipients,
  performInhabitantsMassAction,
  updateInhabitantAddress,
} from '../../../../api'
import { useMassActions, useNotification, useUk } from '../../../../hooks'
import {
  brToNewLine,
  getMassActionExcludeIdParam,
  getMassActionIdParam,
  newLineToBr,
} from '../../../../utils/common'
import { stopPropagation } from '../../../../utils/events'
import { getIdFilterParam } from '../../../../utils/filter'
import { ConfirmationModal } from '../../../Confirmation'
import { useSendEmail } from '../../../Email'
import { getEntityActionTypeSelectOptionsEditOnly } from '../../../Entity'
import {
  FORMIK_ENTITY_ACTION_CONFIG_FIELDS_TYPE_SELECT_NAME,
  getFormikEntityActionConfigFieldsInitialValuesPayload,
} from '../../../Formik'
import {
  getUserEditRoleFormModalInitialValues,
  USER_INVITATION_FORM_FIELD_NAME_MESSAGE,
  USER_INVITATION_FORM_FIELD_NAME_SUBJECT,
  USER_INVITATION_FORM_FIELD_WITH_SMS,
  UserEditAddressFormModal,
  UserEditRoleFormModal,
  UserInvitationFormModal,
} from '../../../User'
import {
  INHABITANT_ACTION_APPROVE_REGISTRATION_ITEM,
  INHABITANT_ACTION_APPROVE_REGISTRATION_MASS,
  INHABITANT_ACTION_ARCHIVE_ITEM,
  INHABITANT_ACTION_ARCHIVE_MASS,
  INHABITANT_ACTION_BLOCK_ITEM,
  INHABITANT_ACTION_BLOCK_MASS,
  INHABITANT_ACTION_CHANGE_ADDRESS,
  INHABITANT_ACTION_EDIT_ROLE_ITEM,
  INHABITANT_ACTION_EDIT_ROLE_MASS,
  INHABITANT_ACTION_MASS_EMAIL,
  INHABITANT_ACTION_RESTORE_ITEM,
  INHABITANT_ACTION_RESTORE_MASS,
  INHABITANT_ACTION_SEND_INVITE_ITEM,
  INHABITANT_ACTION_SEND_INVITE_MASS,
  INHABITANT_ACTION_UNBLOCK_ITEM,
  INHABITANT_ACTION_UNBLOCK_MASS,
  INHABITANT_TABLE_MASS_ACTION_NAMES,
} from '../../Inhabitant.constants'
import {
  getInhabitantTableAsyncNotificationText,
  getInhabitantTableMassActionStatusApiParam,
  getInhabitantTableNotificationTitle,
  isInhabitantActionEditRoleMass,
} from '../../Inhabitant.utils'

import { getUserInvitationFormInitialValues } from './useInhabitantTableActions.utils'

export function useInhabitantTableActions({
  excludeIds,
  filterValues,
  pagination,
  selectAllPages,
  selectedIds,
  sub,
  templateEntityApiParams,
  unselectAllPages,
  onCleanSelection,
  onCloseEmailFormModal,
  onCloseModal,
  onReload,
  onSetEmailFormModal,
  onSetModal,
}) {
  const location = useLocation()

  const { t } = useTranslation()

  const ukData = useUk()

  const [isPerformingMassAction, setPerformingMassAction] = useState(false)

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  const {
    getAsyncTaskId,
    handleAddAsyncMassActionNotification,
    handleAddFinishMassActionNotification,
  } = useMassActions({
    getAsyncNotificationText: getInhabitantTableAsyncNotificationText,
    getNotificationTitle: getInhabitantTableNotificationTitle,
  })

  const { handleOpenCreationEmail } = useSendEmail({
    excludeIds,
    filterValues,
    getRecipients: getInhabitancyEmailRecipients,
    selectAllPages,
    selectedIds,
    templateEntityApiParams,
    onCloseEmailFormModal,
    onCloseModal,
    onSetEmailFormModal,
    onSetModal,
    onSuccessSending: unselectAllPages,
  })

  function handleChangeAddress(item) {
    setItemAction(INHABITANT_ACTION_CHANGE_ADDRESS, item)
  }

  function massAction({ action, actionType, ids, value }) {
    const id = getMassActionIdParam({ ids, selectAllPages, selectedIds })
    const exclude_id = getMassActionExcludeIdParam({
      excludeIds,
      selectAllPages,
    })

    const status = getInhabitantTableMassActionStatusApiParam(
      action,
      filterValues.status
    )
    const archived = isInhabitantActionEditRoleMass(action)
      ? false
      : filterValues.archived

    const params = {
      ...getFormikEntityActionConfigFieldsInitialValuesPayload(actionType),
      action: INHABITANT_TABLE_MASS_ACTION_NAMES[action],
      all: selectAllPages,
      filters: {
        ...filterValues,
        archived,
        exclude_id,
        id: getIdFilterParam({
          filterValues,
          ids: id,
          selectAllPages,
        }),
        status,
      },
      payload: { ...value },
    }

    const countOfItems = selectAllPages ? pagination.count : id.length

    onCloseModal()

    setPerformingMassAction(true)

    performInhabitantsMassAction({ data: params })
      .then(({ data }) => {
        onCloseModal()
        onCleanSelection()

        const asyncTask = getAsyncTaskId(data)

        if (asyncTask) {
          handleAddAsyncMassActionNotification({
            action,
            pathname: location.pathname,
            sub,
            task: asyncTask,
            totalCount: countOfItems,
          })

          return
        }

        onReload()

        handleAddFinishMassActionNotification(data, {
          action,
          totalCount: countOfItems,
        })
      })
      .catch(() => {
        onCloseModal()
        handleAddErrorNotification('Notification.InhabitantMassActionErrorKey')
      })
      .finally(() => {
        setPerformingMassAction(false)
      })
  }

  function showConfirmModal(title, onOk, btnText) {
    onSetModal(
      <ConfirmationModal
        actionButtonsProps={{
          submitText: btnText,
        }}
        description={title}
        qaName='confirmation'
        onCancel={onCloseModal}
        onSubmit={onOk}
      />
    )
  }

  function changeAddress({ action, id, value }) {
    updateInhabitantAddress({ id, value })
      .then(() => {
        onCloseModal()
        onReload()
        handleAddSuccessNotification(
          getInhabitantTableNotificationTitle({ action, t })
        )
      })
      .catch(() => {
        onCloseModal()
        handleAddErrorNotification(
          'Notification.InhabitantChangeAddressErrorKey'
        )
      })
  }

  function getHandleSubmitActionWithType({ action, id }) {
    return ({ payload, values }) => {
      massAction({
        ...(id ? { ids: [id] } : null),
        action,
        actionType: values[FORMIK_ENTITY_ACTION_CONFIG_FIELDS_TYPE_SELECT_NAME],
        value: payload,
      })
    }
  }

  function getHandlerSubmitInvitationModal({ action, item }) {
    return values => {
      const subject = values[USER_INVITATION_FORM_FIELD_NAME_SUBJECT]

      return massAction({
        action,
        ids: item?.id,
        value: {
          ...(subject ? { subject } : null),
          message: newLineToBr(values[USER_INVITATION_FORM_FIELD_NAME_MESSAGE]),
          send_sms: values[USER_INVITATION_FORM_FIELD_WITH_SMS],
        },
      })
    }
  }

  const invitationFormInitialValues = getUserInvitationFormInitialValues({
    message: brToNewLine(ukData.config.invitation_text_for_dwellers),
    subject: ukData.config.invitation_subject_for_dwellers,
  })

  function setItemAction(action, item) {
    switch (action) {
      case INHABITANT_ACTION_ARCHIVE_ITEM:
        showConfirmModal(
          t('Modal.InhabitantArchiveConfirmationTextKey'),
          e => {
            stopPropagation(e)
            massAction({ action, ids: item.id })
          },
          t('Modal.InhabitantArchiveBtnKey')
        )
        break
      case INHABITANT_ACTION_RESTORE_ITEM:
        showConfirmModal(
          t('Modal.InhabitantRestoreConfirmationTextKey'),
          e => {
            stopPropagation(e)
            massAction({ action, ids: item.id })
          },
          t('Modal.InhabitantRestoreBtnKey')
        )
        break
      case INHABITANT_ACTION_BLOCK_ITEM:
        showConfirmModal(
          t('Modal.InhabitantBlockConfirmationTextKey'),
          e => {
            stopPropagation(e)
            massAction({ action, ids: item.id, value: { status: 3 } })
          },
          t('Modal.InhabitantBlockBtnKey')
        )
        break
      case INHABITANT_ACTION_UNBLOCK_ITEM:
        showConfirmModal(
          t('Modal.InhabitantUnblockConfirmationTextKey'),
          e => {
            stopPropagation(e)
            massAction({ action, ids: item.id, value: { status: 2 } })
          },
          t('Modal.InhabitantUnblockBtnKey')
        )
        break
      case INHABITANT_ACTION_APPROVE_REGISTRATION_ITEM:
        showConfirmModal(
          t('Modal.InhabitantApproveConfirmationTextKey'),
          e => {
            stopPropagation(e)
            massAction({ action, ids: item.id })
          },
          t('Modal.InhabitantApproveBtnKey')
        )
        break
      case INHABITANT_ACTION_EDIT_ROLE_ITEM:
        onSetModal(
          <UserEditRoleFormModal
            formFieldsProps={{
              typeSelectProps: {
                disabled: true,
                options: getEntityActionTypeSelectOptionsEditOnly(),
              },
            }}
            formTitle='Modal.ChangeRoleTitleKey'
            initialValues={getUserEditRoleFormModalInitialValues({
              role: item.role,
              t,
            })}
            onClose={onCloseModal}
            onSubmit={getHandleSubmitActionWithType({ action, id: item.id })}
          />
        )
        break
      case INHABITANT_ACTION_CHANGE_ADDRESS:
        onSetModal(
          <UserEditAddressFormModal
            ownerId={item.id}
            onArchive={() =>
              massAction({
                action: INHABITANT_ACTION_ARCHIVE_ITEM,
                ids: item.id,
              })
            }
            onClose={onCloseModal}
            onSubmit={value => changeAddress({ action, id: item.id, value })}
          />
        )
        break
      case INHABITANT_ACTION_SEND_INVITE_ITEM:
        onSetModal(
          <UserInvitationFormModal
            initialValues={invitationFormInitialValues}
            submitButtonText='Modal.SendBtnKey'
            onClose={onCloseModal}
            onSubmit={getHandlerSubmitInvitationModal({ action, item })}
          />
        )
        break
      default:
        return null
    }
  }

  function setMassAction(action) {
    switch (action) {
      case INHABITANT_ACTION_ARCHIVE_MASS:
        showConfirmModal(
          t('Modal.InhabitantMassArchiveConfirmationTextKey'),
          e => {
            stopPropagation(e)
            massAction({ action })
          },
          t('Modal.InhabitantMassArchiveBtnKey')
        )
        break
      case INHABITANT_ACTION_RESTORE_MASS:
        showConfirmModal(
          t('Modal.InhabitantMassRestoreConfirmationTextKey'),
          e => {
            stopPropagation(e)
            massAction({ action })
          },
          t('Modal.InhabitantMassRestoreBtnKey')
        )
        break
      case INHABITANT_ACTION_BLOCK_MASS:
        showConfirmModal(
          t('Modal.InhabitantMassBlockConfirmationTextKey'),
          e => {
            stopPropagation(e)
            massAction({ action, value: { status: 3 } })
          },
          t('Modal.InhabitantMassBlockBtnKey')
        )
        break
      case INHABITANT_ACTION_UNBLOCK_MASS:
        showConfirmModal(
          t('Modal.InhabitantMassUnblockConfirmationTextKey'),
          e => {
            stopPropagation(e)
            massAction({ action, value: { status: 2 } })
          },
          t('Modal.InhabitantMassUnblockBtnKey')
        )
        break
      case INHABITANT_ACTION_APPROVE_REGISTRATION_MASS:
        showConfirmModal(
          t('Modal.InhabitantMassApproveConfirmationTextKey'),
          e => {
            stopPropagation(e)
            massAction({ action })
          },
          t('Modal.InhabitantMassApproveBtnKey')
        )
        break
      case INHABITANT_ACTION_EDIT_ROLE_MASS:
        onSetModal(
          <UserEditRoleFormModal
            formFieldsProps={{
              typeSelectProps: {
                disabled: true,
                options: getEntityActionTypeSelectOptionsEditOnly(),
              },
            }}
            formTitle='Modal.ChangeRoleTitleKey'
            initialValues={getUserEditRoleFormModalInitialValues({ t })}
            needToCheckNoChanges={false}
            onClose={onCloseModal}
            onSubmit={getHandleSubmitActionWithType({ action })}
          />
        )
        break
      case INHABITANT_ACTION_SEND_INVITE_MASS:
        onSetModal(
          <UserInvitationFormModal
            initialValues={invitationFormInitialValues}
            submitButtonText='Modal.SendBtnKey'
            onClose={onCloseModal}
            onSubmit={getHandlerSubmitInvitationModal({ action })}
          />
        )
        break
      case INHABITANT_ACTION_MASS_EMAIL:
        handleOpenCreationEmail()
        break
      default:
        return null
    }
  }

  return {
    handleChangeAddress,
    isPerformingMassAction,
    setItemAction,
    setMassAction,
  }
}
